import { applyKnot, KNOT_BEHAVIOUR, type KnotType } from './knots';
import { PatternError } from './normal';
import type { ChainModule, ChainPattern, ColorIndex, MotifKind } from './types';

/**
 * Daisy-chain / motif-chain bracelets.
 *
 * These are not grids. Six strands are held in a fixed setup order and worked
 * as a sequence of modules: a run of chain, then a motif, then more chain.
 *
 * Setup, left to right (strand ids 0-5):
 *
 *      0        1        2       3       4        5
 *   [chain] [accent] [motif] [motif] [accent] [chain]
 *
 * The construction below follows the widely taught daisy-chain method (Moms
 * and Crafters / braceletbook tutorial #154, itself crediting Masha Knots).
 * The motif is built as a small diamond: the two motif strands travel outwards
 * over the accent and chain strands, the accent strands meet in the middle to
 * form the visible centre dot, then the motif strands come back in and close.
 *
 * The property that makes this safe to generate instructions from: EVERY module
 * restores the setup order. A motif that left the strands scrambled would make
 * the next motif come out a different colour. `simulateChain` asserts this.
 */

export const CHAIN_STRAND_COUNT = 6;

export const STRAND_ROLES = ['chain', 'accent', 'motif', 'motif', 'accent', 'chain'] as const;
export type StrandRole = 'chain' | 'accent' | 'motif';

export interface ChainKnot {
  /** Position of the LEFT string of the pair, 0-4. */
  position: number;
  knot: KnotType;
  /** Strand doing the knotting, resolved during simulation. */
  workingStrand: number;
  /** Colour that shows. */
  color: ColorIndex;
  /** Plain-language instruction fragment for the guide. */
  action: string;
  /** Which module produced this knot. */
  moduleIndex: number;
  /** Sub-step label within the module, e.g. "petals out". */
  phase: string;
}

export interface SimulatedModule {
  index: number;
  module: ChainModule;
  knots: ChainKnot[];
  title: string;
}

export interface ChainStrandSummary {
  id: number;
  role: StrandRole;
  color: ColorIndex;
  knots: number;
  /** Single-knot equivalents of thread load; doubled knots count twice. */
  hitchPairs: number;
  label: string;
}

export interface ChainSimulation {
  modules: SimulatedModule[];
  strands: ChainStrandSummary[];
  totalKnots: number;
  motifCount: number;
}

/** Human-facing description of each motif, used in the guide. */
export const MOTIF_INFO: Record<
  MotifKind,
  {
    name: string;
    centreName: string;
    bodyName: string;
    chainName: string;
    blurb: string;
    /**
     * How many lobes the preview should draw per round. This is a drawing hint
     * for the picture at the top of the screen, NOT a knot-level fact: the knot
     * sequence is the same for every motif here and only the colour roles and
     * chain lengths differ. See the README's "what I could not verify".
     */
    petalsPerRound: number;
  }
> = {
  daisy: {
    name: 'daisy',
    centreName: 'flower centre',
    bodyName: 'petals',
    chainName: 'background chain',
    blurb: 'A round flower with a contrasting centre dot, spaced along a plain chain.',
    petalsPerRound: 4,
  },
  cherry: {
    name: 'cherry',
    centreName: 'cherry highlight',
    bodyName: 'cherry body',
    chainName: 'stem',
    blurb: 'A small round fruit on a green stem chain.',
    petalsPerRound: 3,
  },
  strawberry: {
    name: 'strawberry',
    centreName: 'seeds',
    bodyName: 'berry',
    chainName: 'stem',
    blurb: 'A berry with a lighter seed dot, on a green stem chain.',
    petalsPerRound: 3,
  },
  heart: {
    name: 'heart',
    centreName: 'highlight',
    bodyName: 'heart body',
    chainName: 'background chain',
    blurb: 'A small solid heart-ish diamond with a highlight, on a plain chain.',
    petalsPerRound: 2,
  },
};

/* -------------------------------------------------------------------------- */
/* Module expansion                                                           */
/* -------------------------------------------------------------------------- */

interface PlannedKnot {
  position: number;
  knot: KnotType;
  action: string;
  phase: string;
}

/**
 * A run of plain chain. The outermost strand on each side ties a knot that
 * shows its own colour and leaves it in place (forward-backward on the left,
 * backward-forward on the right), so it stacks into a neat column down the
 * edge without travelling. Both sides get the same count so the bracelet stays
 * square.
 */
function planChainSegment(knotsPerSide: number): PlannedKnot[] {
  const out: PlannedKnot[] = [];
  for (let i = 0; i < knotsPerSide; i++) {
    out.push({
      position: 0,
      knot: 'FB',
      action: 'left chain string ties a forward-backward knot around the string next to it',
      phase: 'chain',
    });
    out.push({
      position: 4,
      knot: 'BF',
      action: 'right chain string ties a backward-forward knot around the string next to it',
      phase: 'chain',
    });
  }
  return out;
}

/**
 * One motif. Nine knots per round, and the strand order is back where it
 * started at the end.
 */
function planMotif(rounds: number): PlannedKnot[] {
  const out: PlannedKnot[] = [];
  for (let r = 0; r < rounds; r++) {
    // 1. Join the two motif strands at the top of the diamond.
    out.push({
      position: 2,
      knot: 'F',
      action: 'tie the two middle strings together to start the motif',
      phase: 'open',
    });

    // 2. Right motif strand travels out over the accent, then locks against
    //    the chain string without moving.
    out.push({
      position: 3,
      knot: 'F',
      action: 'right middle string forward-knots over the accent string, moving outwards',
      phase: 'spread',
    });
    out.push({
      position: 4,
      knot: 'FB',
      action: 'right middle string ties a forward-backward knot against the chain string and stays put',
      phase: 'spread',
    });

    // 3. Mirror on the left.
    out.push({
      position: 1,
      knot: 'B',
      action: 'left middle string backward-knots over the accent string, moving outwards',
      phase: 'spread',
    });
    out.push({
      position: 0,
      knot: 'BF',
      action: 'left middle string ties a backward-forward knot against the chain string and stays put',
      phase: 'spread',
    });

    // 4. The accent strands have been pushed into the middle: knot them for the
    //    visible centre dot.
    out.push({
      position: 2,
      knot: 'F',
      action: 'tie the two accent strings together — this is the visible centre of the motif',
      phase: 'centre',
    });

    // 5. Motif strands come back in.
    out.push({
      position: 1,
      knot: 'F',
      action: 'left middle string forward-knots back towards the centre',
      phase: 'close',
    });
    out.push({
      position: 3,
      knot: 'B',
      action: 'right middle string backward-knots back towards the centre',
      phase: 'close',
    });

    // 6. Close the diamond.
    out.push({
      position: 2,
      knot: 'F',
      action: 'tie the two middle strings together to close the motif',
      phase: 'close',
    });
  }
  return out;
}

export function planModule(module: ChainModule): PlannedKnot[] {
  return module.kind === 'chain'
    ? planChainSegment(module.knots)
    : planMotif(Math.max(1, module.rounds));
}

/* -------------------------------------------------------------------------- */
/* Simulation                                                                 */
/* -------------------------------------------------------------------------- */

export function simulateChain(pattern: ChainPattern): ChainSimulation {
  if (pattern.strandColors.length !== CHAIN_STRAND_COUNT) {
    throw new PatternError(
      `A chain bracelet uses exactly ${CHAIN_STRAND_COUNT} strands; got ${pattern.strandColors.length}.`,
    );
  }

  const setupOrder = Array.from({ length: CHAIN_STRAND_COUNT }, (_, i) => i);
  const setupColors = pattern.strandColors.slice();
  const order = setupOrder.slice();
  const knotsPerStrand = new Array<number>(CHAIN_STRAND_COUNT).fill(0);
  const hitchPairsPerStrand = new Array<number>(CHAIN_STRAND_COUNT).fill(0);
  const modules: SimulatedModule[] = [];
  let totalKnots = 0;
  let motifCount = 0;

  pattern.modules.forEach((module, moduleIndex) => {
    const planned = planModule(module);
    const knots: ChainKnot[] = [];

    for (const p of planned) {
      const left = order[p.position]!;
      const right = order[p.position + 1]!;
      const res = applyKnot(p.knot, left, right);
      order[p.position] = res.left;
      order[p.position + 1] = res.right;
      knotsPerStrand[res.shown]! += 1;
      hitchPairsPerStrand[res.shown]! += KNOT_BEHAVIOUR[p.knot].hitchPairs;
      totalKnots++;
      knots.push({
        position: p.position,
        knot: p.knot,
        workingStrand: res.shown,
        color: pattern.strandColors[res.shown]!,
        action: p.action,
        moduleIndex,
        phase: p.phase,
      });
    }

    // The invariant that makes the whole style work.
    //
    // Checked on COLOURS, not strand ids. A symmetric motif genuinely does
    // trade the two motif strands with each other and the two accent strands
    // with each other — they are mirror partners of the same colour, so the
    // next motif comes out identical. Demanding that strand ids come home
    // would reject a construction that is correct in the hand. Demanding that
    // colours come home catches the failure that actually matters: a motif
    // that would make the NEXT motif a different colour.
    const colorsNow = order.map((s) => pattern.strandColors[s]!);
    if (!sameOrder(colorsNow, setupColors)) {
      throw new PatternError(
        `Module ${moduleIndex + 1} (${module.kind}) left the strings in colour order ` +
          `[${colorsNow.join(',')}] instead of [${setupColors.join(',')}]. Every module must ` +
          `return the strings to the setup order or the next motif comes out wrong.`,
      );
    }
    assertPermutation(order);

    if (module.kind === 'motif') motifCount++;
    modules.push({
      index: moduleIndex,
      module,
      knots,
      title:
        module.kind === 'chain'
          ? `Chain — ${module.knots} knot${module.knots === 1 ? '' : 's'} on each side`
          : `${capitalize(MOTIF_INFO[module.motif].name)} motif`,
    });
  });

  const strands: ChainStrandSummary[] = setupOrder.map((id) => ({
    id,
    role: STRAND_ROLES[id]!,
    color: pattern.strandColors[id]!,
    knots: knotsPerStrand[id]!,
    hitchPairs: hitchPairsPerStrand[id]!,
    label: strandLabel(id),
  }));

  return { modules, strands, totalKnots, motifCount };
}

function strandLabel(id: number): string {
  switch (id) {
    case 0:
      return 'String 1 — left chain string';
    case 1:
      return 'String 2 — left accent string';
    case 2:
      return 'String 3 — left motif string';
    case 3:
      return 'String 4 — right motif string';
    case 4:
      return 'String 5 — right accent string';
    default:
      return 'String 6 — right chain string';
  }
}

function sameOrder(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function assertPermutation(order: number[]): void {
  const seen = new Set(order);
  if (seen.size !== CHAIN_STRAND_COUNT) {
    throw new PatternError(
      `Chain simulation lost a strand: order [${order.join(',')}] is not a permutation.`,
    );
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Approximate finished length of a chain bracelet, in cm.
 *
 * A chain knot advances the work by roughly one knot height, and a motif is a
 * diamond about four knots tall per round. Tension matters more here than in
 * any other style, which is why the guide tells her to measure after a few
 * motifs rather than trusting this.
 */
export function chainLengthCm(pattern: ChainPattern, rowsPerCm: number): number {
  let rows = 0;
  for (const m of pattern.modules) {
    rows += m.kind === 'chain' ? m.knots : 4 * Math.max(1, m.rounds);
  }
  return rows / rowsPerCm;
}

/** How many rows tall a module is, used for the preview drawing. */
export function moduleRows(m: ChainModule): number {
  return m.kind === 'chain' ? m.knots : 4 * Math.max(1, m.rounds);
}
